import type { InternalNoteColumnsFragment } from '@graphql/generated';
import { AuthService } from '../../auth/auth.service';

/**
 * Order Guide note shown on the dashboard. Backed by cmt."Note" (see InternalNote); shape mirrors
 * CFD notes. Notes staged in a dialog but not yet saved carry a negative `id`.
 */
export interface OrderGuideNote {
  id: number;
  text: string;
  author: string;
  createdOn: string;
}

/** Map a persisted InternalNote (from GraphQL) to the dashboard note shape. */
export function internalNoteToOrderGuideNote(note: InternalNoteColumnsFragment): OrderGuideNote {
  return {
    id: note.id,
    text: note.noteText,
    author: note.userId,
    createdOn:
      typeof note.createdOn === 'string' ? note.createdOn : new Date(note.createdOn).toISOString()
  };
}

/** Oldest first. */
export function sortOrderGuideNotes(notes: readonly OrderGuideNote[] | null | undefined): OrderGuideNote[] {
  return [...(notes ?? [])].sort(
    (a, b) => new Date(a.createdOn).getTime() - new Date(b.createdOn).getTime()
  );
}

export function createOrderGuideNote(text: string, auth: AuthService, nextId: number): OrderGuideNote {
  return {
    id: nextId,
    text,
    author: auth.getActiveAccount()?.name?.trim() || 'unknown',
    createdOn: new Date().toISOString(),
  };
}

/** `MM/DD/YYYY` for order guide note rows. */
export function formatOrderGuideNoteDate(createdOn: string | Date): string {
  const date = new Date(createdOn);
  if (Number.isNaN(date.getTime())) {
    return '';
  }
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  const year = date.getFullYear();
  return `${month}/${day}/${year}`;
}
